// services/api.js
import { getConfig } from '../config.js';
import {
  answerTextMappings,
  calculateRecommendation,
  formatRecommendation,
  getElevatedClub,
  getElevatedClubUrl
} from '../stores/surveyStore.js';

// Get environment configuration
const config = getConfig();

/**
 * Generate a DynamoDB-formatted timestamp string.
 * Exported so SurveyContainer can call it once at session start.
 */
export function generateTimestamp() {
  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
         `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.` +
         `${pad(now.getMilliseconds(), 6)}`;
}

/**
 * Format survey data for API submission with exact DynamoDB structure
 * @param {Object} surveyData - Survey responses
 * @param {string} campaign - Campaign name
 * @param {Object} recommendationInfo - Optional recommendation data
 * @param {Object} dropOffInfo - Optional drop-off tracking data
 * @returns {Object} Formatted data for DynamoDB
 */
export function formatSurveyForAPI(surveyData, campaign = "Cavs United Recommendations", recommendationInfo = null, dropOffInfo = null) {
  console.log('Raw survey data before formatting:', surveyData);
  
  // Use the session sk if available (set once at survey start), else fall back to now
  const sk = surveyData.sk || generateTimestamp();
  // lastUpdatedAt always reflects the actual time of this save
  const lastUpdatedAt = generateTimestamp();
  
  // Start with the required DynamoDB fields
  const apiData = {
    "campaign": campaign
  };

  // Always write email if present
  if (surveyData.email) {
    apiData.email = surveyData.email;
  }

  // isAnonymous: respect whatever the caller explicitly passes.
  // - saveIntermediate always passes isAnonymous: true (even if email is known)
  // - submitSurvey passes the raw store, so isAnonymous is not set → false when email exists 
  apiData.isAnonymous = surveyData.isAnonymous === true
    ? true
    : !!surveyData.email === false; // no email on final submit → still anonymous
  
  if (surveyData.source) {
    apiData.source = surveyData.source;
  }

  apiData.sk = sk;
  apiData.lastUpdatedAt = lastUpdatedAt;

  if (dropOffInfo) {
    apiData.dropOffQues = dropOffInfo.question;
    apiData.numOfQues = dropOffInfo.numOfQues;
  }

  // Question 1: Inspiration - check all possible keys
  const inspirationId = surveyData.inspiration || surveyData.q1 || surveyData['multi-choice-1'] || '';
  if (inspirationId) {
    apiData["What most inspires you to explore Cavs United today?"] = 
      answerTextMappings.inspiration[inspirationId] || inspirationId;
  }
  
  // Question 2: Games wanted - check all possible keys
  const attendanceId = surveyData.attendance || surveyData.q2 || surveyData['multi-choice-2'] || '';
  if (attendanceId) {
    apiData["How many games would you want to attend monthly?"] = 
      answerTextMappings.attendance[attendanceId] || attendanceId;
  }
  
  // Question 3: Seating - check all possible keys including image-multi-choice
  const seatingId = surveyData.seating || surveyData.q3 || surveyData['seating-3'] || 
                   surveyData['image-multi-choice-3'] || '';
  if (seatingId) {
    apiData["When you come out to Cavs games, where do you typically like to sit?"] =
      answerTextMappings.seating[seatingId] || seatingId;
  }

  // Question 3.5: Elevated experience (food & drink) - only asked for Loudville/Lower Bowl
  const elevatedId = surveyData.elevatedExperience || surveyData['q3.5'] ||
                     surveyData['multi-choice-3.5'] || '';
  if (elevatedId) {
    apiData["Would you like an elevated experience including food and drink?"] =
      answerTextMappings.elevatedExperience[elevatedId] || elevatedId;
  }

  // Question 4: Companions (multi-select) - check all possible keys
  const companionsData = surveyData.companions || surveyData.q4 || surveyData['multi-choice-4'] || 
                        surveyData['multi-select-4'];
  if (companionsData) {
    let selectedCompanions = [];
    
    if (Array.isArray(companionsData)) {
      selectedCompanions = companionsData.map(id => answerTextMappings.companions[id] || id);
    } else if (typeof companionsData === 'object' && !Array.isArray(companionsData)) {
      const selectedIds = Object.entries(companionsData)
        .filter(([_, selected]) => selected === true || selected === 1 || selected === '1')
        .map(([id]) => id);
      selectedCompanions = selectedIds.map(id => answerTextMappings.companions[id] || id);
    } else if (typeof companionsData === 'string') {
      selectedCompanions = [answerTextMappings.companions[companionsData] || companionsData];
    }
    
    apiData["Who do you typically attend Cavs games with?"] = selectedCompanions;
  }
  
  // Question 5/6: Playoff Importance - numbered differently in different places
  const playoffImportanceId = surveyData.playoffImportance || surveyData.q5 || surveyData.q6 || 
                            surveyData['multi-choice-5'] || surveyData['multi-choice-6'] || '';
  if (playoffImportanceId) {
    apiData["How important is playoff ticket access to you?"] = 
      answerTextMappings.playoffImportance[playoffImportanceId] || playoffImportanceId;
  }
  
  // Question 7/10: Perks (Multi-select) - numbered differently in different places
  const perksData = surveyData.perks || surveyData.q7 || surveyData.q10 || 
                  surveyData['multi-choice-7'] || surveyData['multi-select-7'] || 
                  surveyData['multi-choice-10'] || surveyData['multi-select-10'];
  if (perksData) {
    let selectedPerks = [];
    
    if (Array.isArray(perksData)) {
      selectedPerks = perksData.map(id => answerTextMappings.perks[id] || id);
    } else if (typeof perksData === 'object' && !Array.isArray(perksData)) {
      const selectedIds = Object.entries(perksData)
        .filter(([_, selected]) => selected === true || selected === 1 || selected === '1')
        .map(([id]) => id);
      selectedPerks = selectedIds.map(id => answerTextMappings.perks[id] || id);
    } else if (typeof perksData === 'string') {
      selectedPerks = [answerTextMappings.perks[perksData] || perksData];
    }
    
    apiData["Which of these other perks are important to you?"] = selectedPerks;
  }

  const seatCountId = surveyData.seatCount || surveyData.q9 || surveyData['multi-choice-9'] || '';
  if (seatCountId) {
    apiData["How many seats are you interested in?"] = 
      answerTextMappings.seatCount[seatCountId] || seatCountId;
  }
  
  // Add recommendation info if provided
  if (recommendationInfo) {
    apiData["Recommended Package"] = recommendationInfo.packageName;
    // Elevated club (Gilly's / Caesar's) surfaced when the fan opted in and sits
    // in Loudville or the Lower Bowl. Flows through to the SFMC "addedValue" column.
    const elevatedClub = getElevatedClub(seatingId, elevatedId);
    apiData["Package Details"] = {
      games: recommendationInfo.details.games,
      pricing: recommendationInfo.details.pricing || '',
      playoffAccess: recommendationInfo.details.playoffAccess,
      benefits: recommendationInfo.details.benefits,
      addedValue: elevatedClub || ''
    };

    if (recommendationInfo.packageLink) {
      apiData["Package Link"] = recommendationInfo.packageLink;
    }
  }
  
  return apiData;
}

/**
 * Get the appropriate package link based on recommendation
 * @param {string} packageType - Quarter, Half, Full
 * @returns {string} Link to the package
 */
export function getPackageLink(packageType) {
  const packageLinks = {
    "Half": "https://seatgeek.com/cleveland/series/4146b0ac-beb8-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac=",
    "Quarter": "https://seatgeek.com/cleveland/series/316ac9b6-64b9-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac=",
    "Full": "https://seatgeek.com/cleveland/series/45db50fa-b8b8-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac=",
    "Premium": config.stopMembershipSelling
      ? "https://seatgeek.com/cleveland-cavaliers-tickets/ticket-packages?aid=15870&pid=web&rid=25261&utm_source=cavs&utm_medium=web&utm_campaign=25261memberships&utm_content=recommendationtool"
      : "https://www.rocketarena.com/premium?aid=15870&pid=web&rid=25261&utm_source=cavs&utm_medium=web&utm_campaign=25261memberships&utm_content=recommendationtool"
  };
  
  return packageLinks[packageType] || packageLinks["Quarter"];
}

/**
 * Fetch survey questions from the API
 * @returns {Promise<Array>} Transformed questions ready for frontend use
 */
export async function fetchQuestions() {
  try {
    console.log(`Fetching questions from ${config.apiEndpoint}...`);
    const response = await fetch(`${config.apiEndpoint}/questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify({
        campaign: 'Cavs United Recommendations'
      })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return transformQuestionsForFrontend(data);
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
}

/**
 * Save intermediate survey progress (per-step / drop-off capture).
 * Always marks isAnonymous: true — final submitSurvey will flip it if email exists.
 * Fire-and-forget; errors are swallowed so they never block navigation.
 * @param {Object} surveyData - Current survey store state
 * @param {Object} currentQuestion - The question currently displayed
 * @param {number} stepIndex - The current step index
 */
export async function saveIntermediate(surveyData, currentQuestion, stepIndex) {
  try {
    const formattedData = formatSurveyForAPI(
      { ...surveyData, isAnonymous: true },
      "Cavs United Recommendations",
      null,
      {
        question: currentQuestion?.title || `Step ${stepIndex}`,
        numOfQues: stepIndex
      }
    );

    await fetch(`${config.apiEndpoint}/responses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify(formattedData),
    });
  } catch (err) {
    console.warn('Intermediate save failed (non-blocking):', err);
  }
}

/**
 * Generate HTML email template based on recommendation data with SFMC click tracking
 * @param {Object} recommendation - The formatted recommendation object
 * @param {Object} surveyData - The complete survey responses
 * @param {boolean} stopMembershipSelling - Feature flag for deposit messaging
 * @returns {string} HTML template ready for email with tracking aliases
 */
export function generateEmailTemplate(recommendation, surveyData, stopMembershipSelling = false) {
  const attributionSuffix = "&aid=15870&pid=web&rid=25261&utm_source=cavs&utm_medium=web&utm_campaign=25261memberships&utm_content=recommendationtool";

  function appendAttribution(url, suffix) {
    if (!url || typeof url !== 'string') return url;
    const cleanSuffix = suffix.startsWith('&') ? suffix : '&' + suffix;
    if (url.includes('?')) {
      return url + cleanSuffix;
    } else {
      return url + '?' + cleanSuffix.substring(1);
    }
  }

  if (!recommendation || !recommendation.packageName) {
    console.error('Invalid recommendation data provided');
    return '';
  }
  
  const packageType = recommendation.packageName.split(/[\s\/]/)[0];
  const campaignId = 'cavs_membership_rec';
  const packageId = packageType.toLowerCase();

  let level = "";
  if (packageType === "Premium") {
    level = "Premium";
  } else {
    level = recommendation.packageName
      .replace(packageType, '')
      .replace('Plan', '')
      .replace('Membership', '')
      .trim();
  }
  
  const seatingImages = getSeatingImagesForEmail(level);

  // Elevated food & drink club (Gilly's / Caesar's) when the fan opted in.
  const elevatedClub = getElevatedClub(
    surveyData.seating || surveyData['seating-3'] || surveyData.q3 ||
      surveyData['image-multi-choice-3'] || '',
    surveyData.elevatedExperience || surveyData['multi-choice-3.5'] ||
      surveyData['q3.5'] || ''
  );
  const elevatedClubUrl = elevatedClub
    ? appendAttribution(getElevatedClubUrl(elevatedClub), attributionSuffix)
    : null;

  const {
    games = '',
    pricing = '',
    savings = '',
    playoffAccess = ''
  } = recommendation.details || {};
  
  const selectedPerks = getOrganizedPerksForEmail(surveyData);
  
  // Quarter / Half / Full point to the SeatGeek ticket-packages page; Premium books a rep.
  let explorePricingUrl = packageType === "Premium"
    ? getRepresentativeUrlForEmail("Premium")
    : config.ticketPackagesUrl;
  
  const representativeBookingUrl = getRepresentativeUrlForEmail(packageType);
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Your Cavs United Membership Recommendation</title>
  <style type="text/css">
    body, table, td, div, p, a { font-family: Arial, sans-serif; margin: 0; padding: 0; }
    body { width: 100% !important; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table { border-collapse: collapse !important; }
    img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
    .email-container { max-width: 600px; margin: auto; border: 1px solid #DDDDDD; background-color: #ffffff; }
    .content { width: 100%; max-width: 600px; margin: 0 auto; border: 1px solid #DDDDDD; background-color: #ffffff; }
    .body { background-color: #ffffff; padding: 20px; }
    .button-container { text-align: center; width: 100%; }
    .button { background-color: #6A0032; color: white; padding: 12px 20px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block; margin: 10px auto; text-align: center; min-width: 200px; max-width: 80%; }
    .secondary-button { background-color: white; color: #6A0032; border: 2px solid #6A0032; padding: 12px 20px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block; margin: 10px auto; text-align: center; min-width: 200px; max-width: 80%; }
    h1 { color: white; margin: 0; padding: 15px; font-size: 24px; }
    h2 { color: #333; font-size: 22px; margin-top: 25px; margin-bottom: 10px; }
    .detail-row { margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 15px; }
    .detail-label { color: #666; font-weight: bold; }
    .detail-value { color: #333; }
    .perks-section { margin-top: 30px; }
    .perks-grid { display: inline-block; width: 100%; text-align: center; }
    .perk-item { display: inline-block; width: 30%; vertical-align: top; margin-bottom: 20px; text-align: center; }
    .perk-image { width: 100px; height: 100px; margin: 0 auto 10px; }
    .perk-image img { width: 100%; height: auto; border-radius: 4px; }
    .perk-label { font-size: 14px; color: #333; }
    .image-gallery { text-align: center; margin-bottom: 20px; }
    .gallery-image { display: inline-block; width: 30%; margin: 0 1%; }
    .gallery-image img { width: 100%; height: auto; border-radius: 8px; }
    .recommendation-summary { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #eee; }
    .sold-out-notice { background-color: #f8f4e5; border-left: 4px solid #f0ad4e; padding: 15px; margin: 20px 0; border-radius: 4px; }
    .sold-out-notice p { margin: 0; color: #856404; font-weight: 500; }
    .sold-out-notice a { color: #6A0032; text-decoration: underline; font-weight: 600; }
    @media screen and (max-width: 600px) {
      .content { width: 100% !important; max-width: 100% !important; }
      .hide-on-mobile { display: none !important; }
      .gallery-image { width: 92% !important; display: block !important; margin: 0 auto 10px !important; }
      .perk-item { width: 100% !important; display: block !important; margin-bottom: 15px !important; }
      h1 { font-size: 20px !important; }
      h2 { font-size: 18px !important; }
      .detail-row { flex-direction: column !important; }
      .detail-label, .detail-value { width: 100% !important; display: block !important; }
      .button, .secondary-button { display: block !important; text-align: center !important; width: 100% !important; box-sizing: border-box !important; }
      .perks-grid { display: block !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f7f7f7;">
  <div class="email-container" style="border: 1px solid #DDDDDD; margin-top: 10px; margin-bottom: 10px;">
    <table width="100%" border="0" cellpadding="0" cellspacing="0" style="background-color: #000000;">
      <tr>
        <td align="center">
          <img src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/66d86d703647e-1725459824.2223.jpg" alt="Cavs United Black" style="width: 100%; max-width: 600px; display: block;">
        </td>
      </tr>
    </table>

    <table width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td>
          <table class="content" align="center" cellpadding="0" cellspacing="0" border="0" style="border: 1px solid #DDDDDD;">
            <tr>
              <td style="background-color: #6A0032; padding: 15px; text-align: center;">
                <h1 style="color: white; margin: 0; padding: 0; font-size: 24px;">Your Personalized Recommendation</h1>
              </td>
            </tr>
            <tr>
              <td class="body" style="padding: 20px;">
                <div class="image-gallery" style="text-align: center; margin-bottom: 20px;">
                  <div class="gallery-image" style="display: inline-block; width: 30%; margin: 0 1%;">
                    <img src="${seatingImages[0]}" alt="Cavs ${level} experience" style="width: 100%; height: auto; border-radius: 8px;">
                  </div>
                  ${seatingImages.slice(1).map((img) => `
                    <div class="gallery-image hide-on-mobile" style="display: inline-block; width: 30%; margin: 0 1%;">
                      <img src="${img}" alt="Cavs ${level} experience" style="width: 100%; height: auto; border-radius: 8px;">
                    </div>
                  `).join('')}
                </div>
                
                <h2 style="text-align: center; font-size: 22px;">${recommendation.packageName}</h2>
                
                <div class="recommendation-summary" style="margin-bottom: 25px; border-bottom: 1px solid #eee; text-align: center;">
                  <p style="text-align: center; font-size: 16px;">
                    ${packageType === 'Half' ? 
                      `Elevate your fan experience with <b>21</b> Cavs games (<b>1</b> preseason, <b>20</b> regular season) and save <b>25%</b> over single-game pricing!` :
                      getSummaryTextForEmail(packageType, level)
                    }
                  </p>
                </div>

                ${elevatedClub ? `
                <div style="background-color: #1a1a1a; border: 1px solid #BB945C; border-radius: 10px; padding: 16px 20px; margin-bottom: 25px; text-align: center;">
                  <span style="display: block; color: #BB945C; font-size: 12px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 6px;">Elevated Experience</span>
                  <p style="margin: 0; color: #ffffff; font-size: 16px;">Enjoy all-inclusive food &amp; drink with access to ${elevatedClubUrl
                    ? `<a href="${elevatedClubUrl}" target="_blank" style="color: #BB945C; font-weight: bold; text-decoration: underline;">${elevatedClub}</a>`
                    : `<b style="color: #BB945C;">${elevatedClub}</b>`}.</p>
                </div>` : ''}

                <div style="margin-bottom: 30px; padding: 0;">
                  <div class="detail-row" style="display: flex; margin: 0; padding: 12px 0; border-bottom: 1px solid #dddddd;">
                    <div class="detail-label" style="width: 30%; font-weight: bold; padding-right: 15px;">Games</div>
                    <div class="detail-value" style="width: 70%;">${games.replace(/\b\d+\b/g, '<b>$&</b>')}</div>
                  </div>
                  <div class="detail-row" style="display: flex; margin: 0; padding: 12px 0; border-bottom: 1px solid #dddddd;">
                    <div class="detail-label" style="width: 30%; font-weight: bold; padding-right: 15px;">Pricing</div>
                    <div class="detail-value" style="width: 70%;">${stopMembershipSelling 
                      ? '2026–27 memberships will be available closer to the 2026-27 schedule release. You may place a deposit to secure a spot in the priority line and receive an early opportunity to select a membership once sales begin.'
                      : pricing.replace(/\$\d+[\d,\.]*\/mo/g, '<b>$&</b>').replace(/\$[\d,\.]+/g, '<b>$&</b>')}</div>
                  </div>
                  ${savings ? `
                  <div class="detail-row" style="display: flex; margin: 0; padding: 12px 0; border-bottom: 1px solid #dddddd;">
                    <div class="detail-label" style="width: 30%; font-weight: bold; padding-right: 15px;">Savings</div>
                    <div class="detail-value" style="width: 70%;">${savings.replace(/\d+%/g, '<b>$&</b>')}</div>
                  </div>
                  ` : ''}
                  <div class="detail-row" style="display: flex; margin: 0; padding: 12px 0; border-bottom: 1px solid #dddddd;">
                    <div class="detail-label" style="width: 30%; font-weight: bold; padding-right: 15px;">Playoff Access</div>
                    <div class="detail-value" style="width: 70%;">${playoffAccess}</div>
                  </div>
                </div>
                
                <div class="button-container" style="text-align: center; margin: 30px 0; width: 100%;">
                  <div style="text-align: center; margin: 10px 0;">
                    <a href="${explorePricingUrl}" 
                       class="button" 
                       alias="${campaignId}_${packageId}_main_cta"
                       style="background-color: #6A0032; color: white; padding: 12px 20px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block; margin: 0 auto; min-width: 200px; text-align: center;">Explore Availability and Pricing</a>
                  </div>
                </div>
                
                <div class="perks-section" style="margin-top: 30px; text-align: center;">
                  <h2 style="text-align: center; font-size: 22px;">Top benefits within membership</h2>
                  <div class="perks-grid" style="text-align: center; display: inline-block; width: 100%;">
                    ${selectedPerks.map((perk) => `
                      <div class="perk-item" style="display: inline-block; width: 30%; vertical-align: top; margin-bottom: 20px; text-align: center;">
                        <div class="perk-image" style="width: 150px; height: 90px; margin: 0 auto 10px; overflow: hidden; border-radius: 4px; position: relative;">
                          <img src="${perk.image}" alt="${perk.label}" style="width: 100%; height: 100%; object-fit: cover; object-position: center;">
                        </div>
                        <div class="perk-label" style="font-size: 14px; color: #333;">${perk.label}</div>
                      </div>
                    `).join('')}
                  </div>
                  <div style="text-align: center; margin-top: 20px;">
                    <a href="${appendAttribution('https://www.nba.com/cavaliers/tickets/membership-benefits', attributionSuffix)}" 
                       alias="${campaignId}_explore_benefits"
                       style="color: #6A0032;">Explore all benefits →</a>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    <table width="100%" border="0" cellpadding="0" cellspacing="0" style="background-color: #6A0032;">
      <tr>
        <td align="center" style="padding: 20px 0 10px 0;">
          <a href="${appendAttribution('https://www.nba.com/cavaliers/mobile', attributionSuffix)}" 
             target="_blank"
             alias="${campaignId}_footer_app_download">
            <img border="0" width="300" src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/66d86d703647e-1725459824.2223.jpg" alt="Download the Cavs App Today championed by Westfield" style="max-width: 300px; width: 100%;">
          </a>
        </td>
      </tr>
      <tr>
        <td align="center" style="padding: 10px 0;">
          <a href="${appendAttribution('https://www.nba.com/cavaliers/', attributionSuffix)}" 
             target="_blank"
             alias="${campaignId}_footer_logo">
            <img border="0" width="80" src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/6509ab8679d93-1695132550.4991.png" alt="Cavs.com" style="max-width: 80px; width: 100%;">
          </a>
        </td>
      </tr>
      <tr>
        <td align="center" style="padding: 10px 20px; font-family: Arial, Helvetica, sans-serif; color: #ffffff; font-size: 16px;">
          <a style="color: #ffffff; text-decoration: none; margin: 0 5px;" 
             href="${appendAttribution('https://www.nba.com/cavaliers/roster', attributionSuffix)}"
             alias="${campaignId}_footer_roster">Roster</a>
          <a style="color: #ffffff; text-decoration: none; margin: 0 5px;" 
             href="${appendAttribution('https://www.nba.com/cavaliers/schedule', attributionSuffix)}"
             alias="${campaignId}_footer_schedule">Schedule</a>
          <a style="color: #ffffff; text-decoration: none; margin: 0 5px;" 
             href="${appendAttribution('https://www.nba.com/cavaliers/news', attributionSuffix)}"
             alias="${campaignId}_footer_news">News</a>
        </td>
      </tr>
      <tr>
        <td align="center" style="padding: 10px 0;">
          <table width="200" border="0" cellpadding="0" cellspacing="0">
            <tr>
              <td align="center" width="50">
                <a href="${appendAttribution('https://www.facebook.com/Cavs', attributionSuffix)}" 
                   target="_blank"
                   alias="${campaignId}_footer_facebook">
                  <img border="0" width="32" src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/62978dbd83bad-1654099389.5396.png" alt="Facebook" style="width: 32px;">
                </a>
              </td>
              <td align="center" width="50">
                <a href="${appendAttribution('https://www.instagram.com/cavs/', attributionSuffix)}" 
                   target="_blank"
                   alias="${campaignId}_footer_instagram">
                  <img border="0" width="32" src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/65298e42504df-1697222210.3289.png" alt="Instagram" style="width: 32px;">
                </a>
              </td>
              <td align="center" width="50">
                <a href="${appendAttribution('https://www.tiktok.com/@cavs', attributionSuffix)}" 
                   target="_blank"
                   alias="${campaignId}_footer_tiktok">
                  <img border="0" width="32" src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/65298e5c7d774-1697222236.5139.png" alt="TikTok" style="width: 32px;">
                </a>
              </td>
              <td align="center" width="50">
                <a href="${appendAttribution('https://twitter.com/cavs', attributionSuffix)}" 
                   target="_blank"
                   alias="${campaignId}_footer_twitter">
                  <img border="0" width="32" src="https://www.cdn7ziump.com/gws2kher4d/en_us/images/65298e7325ff6-1697222259.1566.png" alt="X" style="width: 32px;">
                </a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td align="center" style="padding: 20px; font-family: Arial, Helvetica, sans-serif; color: #ffffff; font-size: 12px; line-height: 18px;">
          <p style="margin: 10px 0 0 0;">Copyright © 2025 NBA Media Ventures, LLC. All rights reserved. No portion of NBA.com may be duplicated, redistributed or manipulated in any form. This site is operated jointly by NBA.</p>
        </td>
      </tr>
    </table>
  </div>
</body>
</html>
  `;
}

function getSeatingImagesForEmail(level) {
  const baseUrl = 'https://files.rocketmortgagefieldhouse.com/membership-survey-images/';
  
  if (!level) {
    return [`${baseUrl}arena1.jpg`, `${baseUrl}arena2.png`, `${baseUrl}fans1.jpg`];
  }
  
  level = level.toLowerCase().trim();
  
  if (level.includes('lower')) {
    return [`${baseUrl}lower/lower_1.webp`, `${baseUrl}lower/lower_2.webp`, `${baseUrl}lower/lower_3.webp`];
  } else if (level.includes('club')) {
    return [`${baseUrl}club/club_1.webp`, `${baseUrl}club/club_2.webp`, `${baseUrl}club/club_3.webp`];
  } else if (level.includes('loudville')) {
    return [`${baseUrl}loudville/loudville_1.webp`, `${baseUrl}loudville/loudville_2.webp`, `${baseUrl}loudville/loudville_3.webp`];
  } else if (level.includes('premium')) {
    return [`${baseUrl}premium/chairmans.jpg`, `${baseUrl}premium/champions.jpg`, `${baseUrl}premium/signature.jpg`];
  }
  
  return [`${baseUrl}arena1.jpg`, `${baseUrl}arena2.png`, `${baseUrl}fans1.jpg`];
}

function getPackageLinkForEmail(recommendation, stopMembershipSelling = false) {
  const packageType = recommendation.packageName.split(/[\s\/]/)[0];
  
  // When stopMembershipSelling is true, consolidate all packages (including Premium) to ticket-packages page
  if (stopMembershipSelling) {
    return "https://seatgeek.com/cleveland-cavaliers-tickets/ticket-packages";
  }
  
  // When stopMembershipSelling is false, use specific URLs for each package
  const packageLinks = {
    "Quarter": "https://seatgeek.com/cleveland/series/316ac9b6-64b9-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac=",
    "Half": "https://seatgeek.com/cleveland/series/4146b0ac-beb8-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac=",
    "Full": "https://seatgeek.com/cleveland/series/45db50fa-b8b8-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac=",
    "Premium": "https://outlook.office365.com/book/CavaliersTicketSales@Clecavs.onmicrosoft.com/s/NaahNSoxR0WoyLu3TF8Qyg2"
  };
  return packageLinks[packageType] || packageLinks["Quarter"];
}

function getRepresentativeUrlForEmail(packageType) {
  return packageType === "Premium" 
    ? "https://outlook.office365.com/book/CavaliersTicketSales@Clecavs.onmicrosoft.com/s/NaahNSoxR0WoyLu3TF8Qyg2"
    : "https://outlook.office365.com/book/CavaliersTicketSales@Clecavs.onmicrosoft.com/s/8U5lte4K0k2j-IJyBIMZMw2";
}

function getSummaryTextForEmail(packageType, level) {
  const summaries = {
    Quarter: {
      Loudville: "Perfect for new fans - enjoy <b>11</b> Cavs games from our upper level with great views of the entire court!",
      Club: "Enjoy <b>11</b> action-packed Cavs games from our comfortable club level with premium amenities.",
      Lower: "Experience <b>11</b> Cavs games from the lower level for an up-close view of all the excitement!",
      default: "Gain access to <b>11</b> Cavs games (<b>1</b> preseason, <b>10</b> regular season) and save <b>12%</b> over single-game pricing!"
    },
    Half: {
      Loudville: "Step up your fandom with <b>21</b> games from our upper level with fantastic arena views and energy!",
      Club: "Experience <b>21</b> great Cavs moments from our club level with enhanced comfort and amenities!",
      Lower: "Get closer to the action with <b>21</b> games in the lower bowl for an immersive gameday experience!",
      default: "Elevate your fan experience with <b>21</b> Cavs games (<b>1</b> preseason, <b>20</b> regular season) and save <b>16%</b> over single-game pricing!"
    },
    Full: {
      Loudville: "Become a true Cavs fan with all <b>43</b> home games from our energetic upper level seats!",
      Club: "Never miss a moment with all <b>43</b> home games from our club level with premium amenities!",
      Lower: "Experience every play with all <b>43</b> home games from our lower level for the ultimate fan experience!",
      default: "Get the complete Cavs experience with all <b>43</b> home games (<b>2</b> preseason, <b>41</b> regular season) and save <b>31%</b> over single-game pricing!"
    },
    Premium: {
      default: "Foundation is our premium membership, offering access to the finest seats and exclusive club spaces at Rocket Arena and elevated experiences throughout the season and beyond."
    }
  };
  
  return (summaries[packageType] && summaries[packageType][level]) ||
    summaries[packageType]?.default ||
    "Your personalized Cavs United membership recommendation based on your preferences!";
}

function getOrganizedPerksForEmail(surveyData) {
  const baseUrl = 'https://files.rocketmortgagefieldhouse.com/membership-survey-images/benefits/';
  
  const perkMap = {
    a: { image: `${baseUrl}team_shop.png`, label: "Discounts on merchandise and concessions" },
    b: { image: `${baseUrl}membership_events.jpg`, label: "Exclusive events and experiences" },
    c: { image: `${baseUrl}playoff_access.jpg`, label: "Priority access to playoff tickets" },
    d: { image: `${baseUrl}membership_service_rep.jpg`, label: "Dedicated member specialist" },
    e: { image: `${baseUrl}ticket_exchange.png`, label: "No fees on additional tickets" },
    f: { image: `${baseUrl}concerts.png`, label: "Priority access to concerts and other Rocket Arena events" },
    g: { image: `${baseUrl}ticket_exchange.png`, label: "Flexibility to exchange games for others" },
    pp: { image: `${baseUrl}membership_service_rep.jpg`, label: "Flexible Payment Plans" }
  };
  
  const perksData = surveyData.perks || surveyData.q7 || surveyData.q10 || 
                  surveyData['multi-choice-7'] || surveyData['multi-select-7'] || 
                  surveyData['multi-choice-10'] || surveyData['multi-select-10'];
  
  let selectedPerks = [];
  
  if (perksData) {
    if (Array.isArray(perksData)) {
      selectedPerks = perksData;
    } else if (typeof perksData === 'object' && !Array.isArray(perksData)) {
      selectedPerks = Object.entries(perksData)
        .filter(([_, selected]) => selected === true || selected === 1 || selected === '1')
        .map(([id]) => id);
    } else if (typeof perksData === 'string') {
      selectedPerks = [perksData];
    }
  }
  
  const defaultPerks = ['g', 'b', 'pp'];
  
  if (selectedPerks.length === 0) {
    return defaultPerks.map(id => perkMap[id]);
  }
  
  let ticketExchangePerk = selectedPerks.includes('e') ? 'e' : 'g';
  let otherUserSelectedPerks = selectedPerks.filter(p => p !== ticketExchangePerk);
  let finalPerks = [ticketExchangePerk];
  
  for (let i = 0; i < Math.min(otherUserSelectedPerks.length, 2); i++) {
    finalPerks.push(otherUserSelectedPerks[i]);
  }
  
  const defaultFillPerks = ['b', 'pp', 'a', 'c', 'd', 'f'];
  let i = 0;
  while (finalPerks.length < 3 && i < defaultFillPerks.length) {
    if (!finalPerks.includes(defaultFillPerks[i])) {
      finalPerks.push(defaultFillPerks[i]);
    }
    i++;
  }
  
  return finalPerks.slice(0, 3).map(id => perkMap[id] || { image: `${baseUrl}team_shop.png`, label: "Member benefit" });
}

/**
 * Submit the completed survey
 * @param {Object} surveyData - Survey responses from user
 * @returns {Promise<Object>} Submission response
 */
export async function submitSurvey(surveyData) {
  try {
    console.log('Starting survey submission process');
    
    const recommendationCode = calculateRecommendation(surveyData);
    const formattedRecommendation = formatRecommendation(recommendationCode);
    
    const packageType = recommendationCode.split(' / ')[0];
    formattedRecommendation.packageLink = getPackageLink(packageType);

    // Pass the stopMembershipSelling flag to the email template
    const emailHtml = generateEmailTemplate(formattedRecommendation, surveyData, config.stopMembershipSelling);
    
    // On final submit: isAnonymous = false if email provided, true otherwise
    const formattedData = formatSurveyForAPI(
      { ...surveyData, isAnonymous: !surveyData.email },
      "Cavs United Recommendations", 
      formattedRecommendation
    );

    formattedData.emailHtml = emailHtml;
    
    console.log('Sending data to API with recommendation:', formattedData);
    
    const response = await fetch(`${config.apiEndpoint}/responses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify(formattedData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error (${response.status}):`, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }
    
    const responseData = await response.json();
    console.log('API submission successful:', responseData);

    return responseData;
  } catch (error) {
    console.error('Error submitting survey:', error);
    throw error;
  }
}

/**
 * Retrieve a recommendation based on survey answers
 * @param {Object} surveyData - Survey responses
 * @returns {Promise<Object>} Recommendation data
 */
export async function getRecommendation(surveyData) {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        const recommendationCode = calculateRecommendation(surveyData);
        const formattedRecommendation = formatRecommendation(recommendationCode);
        
        const packageType = recommendationCode.split(' / ')[0];
        formattedRecommendation.packageLink = getPackageLink(packageType);
        
        resolve({ success: true, data: formattedRecommendation });
      }, 1000);
    });
  } catch (error) {
    console.error('Error getting recommendation:', error);
    throw error;
  }
}

function transformQuestionsForFrontend(apiQuestions) {
  // parseFloat (not parseInt) so fractional ordering keys like "3.5" sort
  // between "3" and "4" instead of collapsing onto "3".
  const sortedQuestions = [...apiQuestions].sort((a, b) => parseFloat(a.sk) - parseFloat(b.sk));
  
  const transformedQuestions = [{
    id: 'intro',
    type: 'intro',
    title: 'Find Your Perfect Cavs Membership',
    mainParagraph: 'A Cavs membership offers season-long access to Cavs games through quarter, half, and full season package options. Beyond game attendance, members enjoy a range of year-round benefits, including Team Shop & concession discounts, exclusive experiences, and special perks that increase with membership tenure.',
    description: 'Answer a few questions to discover the membership package that best fits your preferences.'
  }];
  
  sortedQuestions.forEach(apiQuestion => {
    const baseQuestionData = {
      id: `q${apiQuestion.sk}`,
      title: apiQuestion.question,
      description: apiQuestion.instructions || ''
    };
    
    switch (apiQuestion.type) {
      case 'informational':
        transformedQuestions.push({ ...baseQuestionData, id: `info-${apiQuestion.sk}`, type: 'info' });
        break;
        
      case 'email':
        transformedQuestions.push({
          ...baseQuestionData,
          id: 'email',
          type: 'email',
          description: 'We\'ll email your personalized membership recommendation.',
          validationRules: apiQuestion.validationRules || {}
        });
        break;

      case 'image multiple choice':
        if (apiQuestion.sk === '3') {
          transformedQuestions.push({
            ...baseQuestionData,
            id: `seating-${apiQuestion.sk}`,
            type: 'multi-choice',
            options: apiQuestion.answers.map(answer => ({
              id: answer.id || answer.text.toLowerCase().replace(/\s+/g, '-'),
              label: answer.text,
              image: answer.imageUrl
            }))
          });
        } else {
          transformedQuestions.push({
            ...baseQuestionData,
            type: 'multi-choice',
            options: apiQuestion.answers.map(answer => ({
              id: answer.id || answer.text.toLowerCase().replace(/\s+/g, '-'),
              label: answer.text,
              image: answer.imageUrl
            }))
          });
        }
        break;
        
      default:
        const questionType = apiQuestion.allowMultipleSelections ? 'multi-select' : 'multi-choice';
        transformedQuestions.push({
          ...baseQuestionData,
          id: apiQuestion.id || `${questionType}-${apiQuestion.sk}`,
          type: questionType,
          options: apiQuestion.answers.map(answer => ({
            id: answer.id || `opt-${answer.order}`,
            label: answer.text,
            ...(answer.imageUrl && { image: answer.imageUrl })
          }))
        });
        break;
    }
  });
  
  const emailIndex = transformedQuestions.findIndex(q => q.id === 'email');
  if (emailIndex > 1) {
    const emailQuestion = transformedQuestions.splice(emailIndex, 1)[0];
    transformedQuestions.splice(1, 0, emailQuestion);
  }
  
  return transformedQuestions;
}